import { EventEmitter } from 'events';

export enum PeaksJSChannel {
    Microphone = "left",
    Sensor = "right"
}

export class PeaksWindow extends Window {
    _peaksJs: PeaksJs;
}

export interface PeaksJs extends EventEmitter {
    container: HTMLElement;
    template: HTMLElement;
    dataUriDefaultFormat: string;
    zoomAdapter: string;
    zoomLevels: number[];
    zoomWaveformColor: string;
    overviewWaveformColor: string;
    axisLabelColor: string;
    axisGridlineColor: string;
    intermediateMajorGridlineColor: string;
    intermediateMinorGridlineColor: string;
    intermediateSize: number;
    randomizeSegmentColor: boolean;
    timeElement: HTMLElement;
    offsetElement: HTMLElement;
    player: PeaksJsPlayer;
    waveform: PeaksJsWaveform;
    zoom: PeaksJsZoom;
    time: PeaksJsTime;
    destroy: () => void;
    isPlaying: () => boolean;
    points: PeaksPoints;
    segments: PeaksSegments;
    currentZoomLevel: number;
    remove: (event: any) => void;
    pointOrSegmentExists: (
        event: PeaksPoint | PeaksPeriod,
        isPoint: boolean
    ) => boolean;
    inEditMode: () => boolean;
    initialTime: number;
    multiDayOffset: number;
    options: any;
}

export interface PeaksJsPlayer {
    offset: number;
    mediaElement: HTMLAudioElement;
    seekBySeconds: (offset: number) => any;
    seek: (percentage: number) => any;
    toggleChannelMute: (channel: PeaksJSChannel) => void;
    play: () => void;
    pause: () => void;
    getDuration: () => number;
}

export interface PeaksJsWaveform {
    toggleGridline: () => void;
    activeSegment: PeaksPeriod;
    activePoint: PeaksPoint;
    points: PeaksPoints;
    segments: PeaksSegments;
}

export interface PeaksJsZoom {
    zoomIn: () => void;
    zoomOut: () => void;
    setZoom: (level: number) => void;
    getZoom: () => number
}

export interface PeaksJsTime {
    setCurrentTime: (offset: number) => void;
    getCurrentTime: () => number;
    getCurrentTimeWithMultiDay: () => number;
}

export interface PeaksPoints {
    add: (pointOrPoints: PeaksPoint | PeaksPoint[]) => void;
    points: PeaksPoint[];
    getPoints: (filter?) => any;
    edit: (point: PeaksPoint) => void;
    remove: (point: PeaksPoint) => void;
    removeAll: () => void;
    getPointLabel: (event: PeaksPoint) => string;
    getPointEventTypeLabel: (event: PeaksPoint) => string;
    editPointTimestamp: (point: PeaksPoint, newTimeInSeconds: number) => boolean;
}

export interface PeaksSegments {
    add: (segmentOrSegments: PeaksPeriod | PeaksPeriod[]) => void;
    segments: PeaksPeriod[];
    edit: (segment: PeaksPeriod) => void;
    remove: (segment: PeaksPeriod) => void;
    getSegments: (filter?) => any;
    getSegmentLabel: (segment: PeaksPeriod) => string;
    getSegmentEventTypeLabel: (segment: PeaksPeriod) => string;
    editSegmentStart: (segment: PeaksPeriod, newTimeInSeconds: number) => void;
    editSegmentEnd: (segment: PeaksPeriod, newTimeInSeconds: number) => void;
}

interface PointBase {
    id: string;
    labelText: string;
    editable: boolean;
    editMode?: boolean;
    color: string;
    eventTypeId: string;
    eventTypeName: string;
    isPoint: boolean;
    layerPosition: number;
    withinDuplicateThreshold: boolean;
    confirmedValidDuplicate: boolean;
}

export interface PeaksPoint extends PointBase {
    timestamp: number;
    rawTimestamp?: number;
    multiDayOffset: number;
}

export function instanceOfPeaksPoint(object: PointBase): object is PeaksPoint {
    return "timestamp" in object;
}

export interface PeaksPeriod extends PointBase {
    startTime: number;
    endTime: number;
    multiDayOffset: number;
    rawStartTime?: number;
    rawEndTime?: number;
}

export enum PeaksJsEventAggregationType {
    Point,
    Period
}

export interface PeaksClick {
    currentPosition: number;
}
